import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faCreditCard, faFileAlt, faExclamationTriangle, faCamera } from '@fortawesome/free-solid-svg-icons';
import { getConsultas, getConsultaMultas, API_URL } from '../../services/api';
import './Consultas.css';

const Consultas = () => {
  const [consultas, setConsultas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    patente: '',
    jurisdiccion: '',
    success: ''
  });
  const [selectedConsulta, setSelectedConsulta] = useState(null);
  const [multas, setMultas] = useState([]);
  const [loadingMultas, setLoadingMultas] = useState(false);

  useEffect(() => {
    fetchConsultas();
  }, [page, filters]);

  const fetchConsultas = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: 20
      };
      if (filters.patente) params.patente = filters.patente;
      if (filters.jurisdiccion) params.jurisdiccion = filters.jurisdiccion;
      if (filters.success !== '') params.success = filters.success;

      const data = await getConsultas(params);
      setConsultas(data.items);
      setTotalPages(data.total_pages);
    } catch (err) {
      setError('Error al cargar consultas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handleSelectConsulta = async (consulta) => {
    setSelectedConsulta(consulta);
    setLoadingMultas(true);
    try {
      const data = await getConsultaMultas(consulta.id);
      setMultas(data.multas || []);
    } catch (err) {
      console.error('Error fetching multas:', err);
      setMultas([]);
    } finally {
      setLoadingMultas(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-AR');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount || 0);
  };

  const buildUrl = (link) => {
    if (!link) return null;
    if (link.startsWith('/api/')) {
      return `${API_URL}${link}`;
    }
    return link;
  };

  return (
    <div className="consultas-page">
      <h1 className="consultas-title">Historial de Consultas</h1>

      <div className="consultas-filters">
        <div className="filter-group">
          <label>Patente</label>
          <input
            type="text"
            name="patente"
            value={filters.patente}
            onChange={handleFilterChange}
            placeholder="Buscar patente..."
          />
        </div>
        <div className="filter-group">
          <label>Jurisdiccion</label>
          <select name="jurisdiccion" value={filters.jurisdiccion} onChange={handleFilterChange}>
            <option value="">Todas</option>
            <option value="GBA">GBA</option>
            <option value="CABA">CABA</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Estado</label>
          <select name="success" value={filters.success} onChange={handleFilterChange}>
            <option value="">Todos</option>
            <option value="true">Exitosas</option>
            <option value="false">Fallidas</option>
          </select>
        </div>
      </div>

      <div className="consultas-layout">
        <div className="consultas-list-container">
          {loading ? (
            <div className="consultas-loading">Cargando consultas...</div>
          ) : error ? (
            <div className="consultas-error">{error}</div>
          ) : (
            <>
              <div className="consultas-table-container">
                <table className="consultas-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Patente</th>
                      <th>Jurisdiccion</th>
                      <th>Infracciones</th>
                      <th>Monto</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consultas.length > 0 ? (
                      consultas.map(consulta => (
                        <tr
                          key={consulta.id}
                          className={selectedConsulta?.id === consulta.id ? 'selected' : ''}
                          onClick={() => handleSelectConsulta(consulta)}
                        >
                          <td className="td-date">{formatDate(consulta.created_at)}</td>
                          <td className="td-patente">{consulta.patente}</td>
                          <td>{consulta.jurisdiccion_codigo}</td>
                          <td className="td-infracciones">
                            <span className={`infraccion-count ${consulta.total_infracciones > 0 ? 'has-infracciones' : ''}`}>
                              {consulta.total_infracciones || 0}
                            </span>
                          </td>
                          <td className="td-monto">
                            {consulta.monto_total ? `$${consulta.monto_total}` : '-'}
                          </td>
                          <td>
                            <span className={`status-badge ${consulta.success ? 'success' : 'failed'}`}>
                              {consulta.success ? 'Exitosa' : 'Fallida'}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="no-data">No hay consultas disponibles</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Anterior
                  </button>
                  <span className="page-info">
                    Pagina {page} de {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Siguiente
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="multas-detail-panel">
          {selectedConsulta ? (
            <>
              <div className="detail-header">
                <h2>Detalle de Multas</h2>
                <p className="detail-patente">{selectedConsulta.patente}</p>
              </div>
              {loadingMultas ? (
                <div className="multas-loading">Cargando multas...</div>
              ) : multas.length > 0 ? (
                <div className="multas-list">
                  {multas.map((multa, index) => (
                    <div key={index} className="multa-card">
                      <div className="multa-header">
                        <span className="multa-acta">Acta: {multa.nroActa}</span>
                        <span className={`multa-estado ${multa.estado?.toLowerCase().includes('paga') ? 'pagada' : ''}`}>
                          {multa.estado}
                        </span>
                      </div>

                      <div className="multa-motivo">{multa.motivo}</div>

                      {multa.lugar && (
                        <div className="multa-lugar">
                          <FontAwesomeIcon icon={faMapMarkerAlt} /> {multa.lugar}
                        </div>
                      )}

                      <div className="multa-details">
                        <div className="multa-detail-item">
                          <span className="label">Tipo/Juzgado:</span>
                          <span className="value">{multa.juzgado}</span>
                        </div>
                        <div className="multa-detail-item">
                          <span className="label">Importe:</span>
                          <span className="value importe">{formatCurrency(multa.importe)}</span>
                        </div>
                        <div className="multa-detail-item">
                          <span className="label">Fecha/Vencimiento:</span>
                          <span className="value">{multa.vencimiento}</span>
                        </div>
                        {multa.jurisdiccion && (
                          <div className="multa-detail-item">
                            <span className="label">Jurisdiccion:</span>
                            <span className="value">{multa.jurisdiccion}</span>
                          </div>
                        )}
                      </div>

                      {multa.detalles && multa.detalles.length > 0 && (
                        <div className="multa-detalles-list">
                          <span className="detalles-title">Infracciones:</span>
                          {multa.detalles.map((det, i) => (
                            <div key={i} className="detalle-item">
                              <span className="detalle-codigo">{det.codigo}</span>
                              <span className="detalle-desc">{det.descripcion}</span>
                              {det.descuenta_puntos !== "0" && (
                                <span className="detalle-puntos">
                                  <FontAwesomeIcon icon={faExclamationTriangle} /> -{det.descuenta_puntos} pts
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="multa-badges">
                        {multa.sePuedePagarOnline && (
                          <span className="feature-badge pago-online">
                            <FontAwesomeIcon icon={faCreditCard} /> Pago Online
                          </span>
                        )}
                        {multa.sePuedeImprimir && (
                          <span className="feature-badge imprimible">
                            <FontAwesomeIcon icon={faFileAlt} /> Imprimible
                          </span>
                        )}
                        {multa.linkPago && (
                          <a href={buildUrl(multa.linkPago)} target="_blank" rel="noopener noreferrer" className="feature-badge pago-online">
                            <FontAwesomeIcon icon={faCreditCard} /> Pagar
                          </a>
                        )}
                        {multa.nroCausa && multa.jurisdiccion === 'GBA' && (
                          <a href={`${API_URL}/api/v1/gba/acta/${multa.nroCausa}`} target="_blank" rel="noopener noreferrer" className="feature-badge imprimible">
                            <FontAwesomeIcon icon={faFileAlt} /> Ver Acta
                          </a>
                        )}
                        {multa.nroCausa && multa.jurisdiccion === 'GBA' && (
                          <a href={`${API_URL}/api/v1/gba/prueba-fotografica/${multa.nroCausa}`} target="_blank" rel="noopener noreferrer" className="feature-badge foto">
                            <FontAwesomeIcon icon={faCamera} /> Foto
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-multas">
                  {selectedConsulta.success
                    ? 'No se encontraron multas para esta patente'
                    : 'La consulta fallo - no hay datos disponibles'}
                </div>
              )}
            </>
          ) : (
            <div className="no-selection">
              <p>Seleccione una consulta para ver el detalle de multas</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Consultas;
